import { Box, Button, Typography } from '@material-ui/core';
import { Add } from '@material-ui/icons';
import { useEffect, useState } from 'react';
import { Title, useGetList } from 'react-admin';
import { useHistory } from 'react-router-dom';
import { useQuizAssessmentAvailabilityDataProvider } from '../../../../providers/data-provider/quiz-assessment-availability.data.provider';
import ApplicationResource from '../../../../utils/resources.enum';
import { ASSESSMENT_TYPE, AssessmentTypeToggle } from './assessment-type-toggle';
import { ClassFilterSection } from './class-filter-section';
import { InfoBanner } from './info-banner';
import { PrintedQuizTable } from './printed-quiz-table';
import { QuizAssessmentsTable } from './quiz-table';
import { PrintedStatCardsRow, StatCardsRow } from './stat-cards-row';
import { useStyles } from './styles';
import { usePrintedAssessmentCorrections } from './use-printed-assessment-corrections';
import {
  useQuizAssessmentTestDataProvider
} from "../../../../providers/data-provider/quiz-assessment-test.data.provider";

const sort = { field: 'start', order: 'DESC' };

const INFO_BANNER_LINES = {
  [ASSESSMENT_TYPE.ONLINE]: 'No modo Online, você acompanha as avaliações disponíveis e encerradas.',
  [ASSESSMENT_TYPE.PRINTED]:
    'No modo Impressa, você acompanha a correção via QR Code e os resultados das avaliações impressas.',
};

// Both lines are always shown, in an order led by the currently selected mode.
const infoBannerLinesFor = (assessmentType) => {
  const other =
    assessmentType === ASSESSMENT_TYPE.ONLINE ? ASSESSMENT_TYPE.PRINTED : ASSESSMENT_TYPE.ONLINE;
  return [INFO_BANNER_LINES[assessmentType], INFO_BANNER_LINES[other]];
};

export function TeacherQuizDashboard() {
  const classes = useStyles();
  const history = useHistory();
  const { listClassesRelated, listAcademicPeriodsRelated, getSummary } = useQuizAssessmentAvailabilityDataProvider();
  const { getAverage, getSummary: getTestSummary } = useQuizAssessmentTestDataProvider()

  const [assessmentType, setAssessmentType] = useState(ASSESSMENT_TYPE.ONLINE);
  const [classesList, setClassesList] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [academicPeriodsList, setAcademicPeriodsList] = useState([]);
  const [selectedAcademicPeriodId, setSelectedAcademicPeriodId] = useState(null);
  const [testSummary, setTestSummary] = useState({ totalActive: 0, totalInactive: 0});
  const [availabilitySummary, setAvailabilitySummary] = useState({
    totalActive: 0, totalInactive: 0, totalAssessments: 0,
  });
  const [average, setAverage] = useState({ totalQuestions: 0, correctAnswers: 0, average: 0 });
  const [vigentFilter, setVigentFilter] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  useEffect(() => {
    listClassesRelated().then((res) => setClassesList(res.data?.data || []));
  }, [listClassesRelated]);

  useEffect(() => {
    listAcademicPeriodsRelated().then((res) => setAcademicPeriodsList(res.data?.data || []));
  }, [listAcademicPeriodsRelated]);

  useEffect(() => {
    getAverage(assessmentType, selectedClassId, selectedAcademicPeriodId).then((res) => {
      setAverage(res.data)
    })

    getSummary(assessmentType, selectedClassId, selectedAcademicPeriodId).then((res) => {
      setAvailabilitySummary(res.data);
    })
    getTestSummary(assessmentType, selectedClassId, selectedAcademicPeriodId).then((res) => {
      setTestSummary(res.data)
    })
  }, [assessmentType, selectedClassId, selectedAcademicPeriodId]);

  const classFilter = {
    ...(selectedClassId ? { classId: selectedClassId } : {}),
    ...(selectedAcademicPeriodId ? { academicPeriodId: selectedAcademicPeriodId } : {}),
  };
  const isPrinted = assessmentType === ASSESSMENT_TYPE.PRINTED;

  const { data: rawData, ids, loading, total } = useGetList(
    ApplicationResource.QUIZ_ASSESSMENT_AVAILABILITY,
    { page, perPage },
    sort,
    { assessmentType: ASSESSMENT_TYPE.ONLINE, vigent: vigentFilter, ...classFilter },
  );

  const {
    data: printedRawData, ids: printedIds, loading: printedLoading, total: printedTotal,
  } = useGetList(
    ApplicationResource.QUIZ_ASSESSMENT_AVAILABILITY,
    { page, perPage },
    sort,
    { assessmentType: ASSESSMENT_TYPE.PRINTED, ...classFilter },
  );

  const records = ids ? ids.map((id) => rawData?.[id]).filter(Boolean) : [];
  const printedRecords = printedIds ? printedIds.map((id) => printedRawData?.[id]).filter(Boolean) : [];
  const correctionByAvailability = usePrintedAssessmentCorrections(isPrinted ? printedRecords : []);


  const handleVigentFilterChange = (newFilter) => {
    setVigentFilter(newFilter);
    setPage(1);
  };

  const handleSelectClass = (classId) => {
    setSelectedClassId(classId);
    setPage(1);
  };

  const handleSelectAcademicPeriod = (academicPeriodId) => {
    setSelectedAcademicPeriodId(academicPeriodId);
    setPage(1);
  };

  const handleAssessmentTypeChange = (newType) => {
    setAssessmentType(newType);
    setPage(1);
  };

  return (
    <Box p={3} width="100%">
      <Title title="Juristta Quiz" />

      <Box className={classes.pageHeaderRow}>
        <Box className={classes.pageHeader}>
          <span className={classes.pageTitle}>Painel de Controle</span>
          <span className={classes.pageSubtitle}>
            Acompanhe as avaliações e o desempenho dos alunos.
          </span>
        </Box>
        <Button
          variant="contained"
          className={classes.newButton}
          startIcon={<Add />}
          onClick={() => history.push(`/${ApplicationResource.QUIZ_ASSESSMENT_AVAILABILITY}/create`)}
        >
          NOVA AVALIAÇÃO
        </Button>
      </Box>

      <Typography className={classes.sectionLabel}>Tipo de Avaliação</Typography>
      <AssessmentTypeToggle value={assessmentType} onChange={handleAssessmentTypeChange} />

      <InfoBanner lines={infoBannerLinesFor(assessmentType)} />

      <ClassFilterSection
        classesList={classesList}
        selectedClassId={selectedClassId}
        onSelectClass={handleSelectClass}
        academicPeriodsList={academicPeriodsList}
        selectedAcademicPeriodId={selectedAcademicPeriodId}
        onSelectAcademicPeriod={handleSelectAcademicPeriod}
      />

      {isPrinted ? (
        <>
          <PrintedStatCardsRow
            createdCount={availabilitySummary.totalAssessments}
            correctedCount={testSummary.finished}
            pendingCount={`${testSummary.started - testSummary.finished}`}
            averageGrade={average.average}
          />

          <PrintedQuizTable
            records={printedRecords}
            total={printedTotal}
            page={page}
            perPage={perPage}
            onPageChange={setPage}
            onPerPageChange={setPerPage}
            correctionByAvailability={correctionByAvailability}
            loading={printedLoading}
          />
        </>
      ) : (
        <>
          <StatCardsRow
            activeCount={availabilitySummary.totalActive}
            closedCount={availabilitySummary.totalInactive}
            participationsCount={`${testSummary.started + testSummary.finished}/${testSummary.totalStudents}`}
            averageGrade={average.average}
          />

          <QuizAssessmentsTable
            records={records}
            total={total}
            page={page}
            perPage={perPage}
            onPageChange={setPage}
            onPerPageChange={setPerPage}
            vigentFilter={vigentFilter}
            onVigentFilterChange={handleVigentFilterChange}
            loading={loading}
          />
        </>
      )}
    </Box>
  );
}

export default TeacherQuizDashboard;